<template>
  <v-dialog
    :value="true"
    max-width="540px"
    @input="!$event && $emit('close')"
  >
    <v-card>
      <v-card-title>{{ $t('Share memory') }}</v-card-title>
      <v-card-text class="share-memory-content">
        <v-alert
          v-if="error"
          type="error"
          dense
          role="alert"
        >
          {{ $t(error) }}
        </v-alert>
        <template v-if="available">
          <v-textarea
            ref="link"
            class="share-link-field"
            :value="shareLink"
            :label="$t('Share by Link')"
            outlined
            :rows="2"
            auto-grow
            no-resize
            readonly
            :loading="checking"
            hide-details
          />
          <div class="share-link-actions">
            <v-btn
              color="primary"
              :disabled="!shareLink || checking"
              :loading="copying"
              @click="copyClipboard"
            >
              {{ $t('Copy link') }}
            </v-btn>
            <v-btn
              v-if="shareLink"
              text
              :href="shareLink"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ $t('Open public page') }}
            </v-btn>
            <v-btn
              v-if="!shareLink && !checking"
              text
              @click="loadLink"
            >
              {{ $t('Retry') }}
            </v-btn>
          </div>
          <p
            v-if="message"
            role="status"
            aria-live="polite"
          >
            {{ $t(message) }}
          </p>
          <v-divider class="my-4" />
          <v-text-field
            v-model="email"
            type="email"
            autocomplete="email"
            :label="$t('Send by email (optional)')"
            outlined
            dense
          />
          <v-textarea
            v-model="description"
            :label="$t('Message (optional)')"
            outlined
            rows="3"
            maxlength="5000"
          />
        </template>
      </v-card-text>
      <v-card-actions>
        <v-btn
          text
          @click="$emit('close')"
        >
          {{ $t('Close') }}
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="available"
          color="primary"
          :disabled="!shareLink || !email || checking"
          :loading="sending"
          @click="sendEmail"
        >
          {{ $t('Send email') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import { canShareRecord, copyPublicLink } from '@/utils/publicShare'

export default {
  props: {
    modals: { type: Object, required: true },
    section: { type: String, required: true },
  },
  data: () => ({
    shareLink: '', checking: false, copying: false, sending: false, error: '', message: '', email: '', description: '',
  }),
  computed: {
    available() { return ['pictures', 'biography'].includes(this.section) && canShareRecord(this.modals.file, this.$store.state.user) },
  },
  watch: {
    available(value) { if (!value) { this.shareLink = ''; this.error = 'Only publicly visible memories can be shared.' } },
  },
  mounted() { this.loadLink() },
  methods: {
    async loadLink() {
      this.error = ''; this.message = ''; this.shareLink = ''
      if (!this.available) {
        this.error = 'Only publicly visible memories can be shared.'

        return
      }
      this.checking = true
      try {
        const { data } = await this.request('link')
        const url = new URL(data.url)
        if (!['https://beta.selfbiography.com', 'https://selfbiography.com'].includes(url.origin)
          || !url.pathname.endsWith(`/${this.section}`) || url.searchParams.get('record') !== String(this.modals.file.id)) throw new Error('Invalid share link')
        if (this.available) this.shareLink = url.toString()
      } catch (error) { this.error = this.failure(error) } finally { this.checking = false }
    },
    request(action) {
      return axios.post(`${process.env.VUE_APP_ROOT_API}/saveMsgMemoryRequest`, {
        action,
        content_id: this.modals.file.id,
        section: this.section,
        ...(action === 'send' ? { email: this.email.trim(), description: this.description } : {}),
      }, { headers: { Authorization: this.$store.state.jwtToken } })
    },
    failure(error) { return (error.response && error.response.data && error.response.data.message) || 'Sharing is unavailable. Please try again.' },
    async copyClipboard() {
      if (!this.available || !this.shareLink || this.copying) return
      this.copying = true; this.error = ''; this.message = ''
      try { await copyPublicLink(this.shareLink); this.message = 'Link copied.' } catch (_) {
        const input = this.$refs.link && this.$refs.link.$refs.input
        if (input) { input.focus(); input.select(); input.setSelectionRange(0, this.shareLink.length) }
        this.error = 'Copying was blocked. Select the link and copy it manually.'
      } finally { this.copying = false }
    },
    async sendEmail() {
      if (!this.available || !this.shareLink || this.sending) return
      this.sending = true; this.error = ''; this.message = ''
      try { await this.request('send'); this.message = 'Email sent.' } catch (error) { this.error = this.failure(error) } finally { this.sending = false }
    },
  },
}
</script>

<style scoped>
.share-memory-content { padding-top: 16px !important; }
.share-link-field ::v-deep textarea { overflow-wrap: anywhere; word-break: break-all; line-height: 1.5; }
.share-link-actions { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0; }
</style>
